import type * as React from "react";
import type { DraggableAttributes } from "@dnd-kit/core";
import { CSS, type Transform } from "@dnd-kit/utilities";

export type SortableStyleOptions = {
  /** dnd-kit's `transition`; applied only while `isSorting`, so a drop lands
   *  without a snap-back animation (the optimistic order already placed it). */
  transition?: string | null | undefined;
  isSorting?: boolean | undefined;
  isDragging?: boolean | undefined;
  /** The axis the list reorders on. The other axis is pinned to 0, so a
   *  vertical list never drifts sideways and a tab row never bobs. */
  axis?: "x" | "y" | undefined;
  /** Translate only, never scale: the sortable transform scales the active
   *  item toward the slot it hovers over, which squashes a wide item dragged
   *  over a narrow one. */
  translateOnly?: boolean | undefined;
  /** The lifted item's opacity while it drags; omit to leave it opaque. */
  dragOpacity?: number | undefined;
};

/**
 * The inline style a dnd-kit sortable node streams every frame. Inline on
 * purpose: `Box` carries no motion props, and a transform that updates per
 * pointer move belongs on the element, not in a class.
 */
export function sortableStyle(
  transform: Transform | null,
  {
    transition,
    isSorting = false,
    isDragging = false,
    axis,
    translateOnly = false,
    dragOpacity,
  }: SortableStyleOptions = {},
): React.CSSProperties {
  const pinned =
    transform && axis === "y"
      ? { ...transform, x: 0 }
      : transform && axis === "x"
        ? { ...transform, y: 0 }
        : transform;
  const serialize = translateOnly ? CSS.Translate.toString : CSS.Transform.toString;
  return {
    transform: serialize(pinned) ?? undefined,
    transition: isSorting ? (transition ?? undefined) : undefined,
    opacity: isDragging ? dragOpacity : undefined,
  };
}

/**
 * dnd-kit's handle props as one spread: the accessibility attributes and the
 * activator listeners, typed for a `Box`. dnd-kit types the listeners as an
 * open map, which is why this is the one place they are re-typed.
 */
export function sortableHandleProps(
  attributes: Partial<DraggableAttributes> | undefined,
  listeners: Record<string, unknown> | undefined,
): React.HTMLAttributes<HTMLElement> {
  return { ...attributes, ...listeners } as React.HTMLAttributes<HTMLElement>;
}

/**
 * Data-attribute markers for dnd-kit drag surfaces — the single source for
 * the marker names. Spread onto any dnd-kit draggable node (useSortable /
 * useDraggable handle) and onto the child of `<DragOverlay>`:
 * `<Box {...DND_DRAGGABLE_PROPS} />`.
 *
 * The attributes do nothing by themselves — the companion CSS ships in this
 * package's `index.css` (`[data-dnd-draggable]` / `[data-dnd-overlay]`).
 * Without it, long-pressing a draggable on iOS triggers native text
 * selection and the system drag ghost instead of a clean dnd-kit drag.
 *
 * That CSS also sets `touch-action: none` on a draggable, which is right for
 * a grip or a tab and wrong for a draggable that IS the scrollable content (a
 * board card fills its column): there a finger could never scroll. Such a
 * node overrides it with `touchAction="manipulation"` — the hold-activated
 * touch sensor below still prevents the scroll once the hold completes, and a
 * swipe that moves before the hold ends is a scroll as it should be.
 */
export const DND_DRAGGABLE_PROPS = {
  "data-dnd-draggable": "true",
} as const;

export const DND_OVERLAY_PROPS = {
  "data-dnd-overlay": "true",
} as const;

/**
 * Activation constraint for the `TouchSensor` every drag surface mounts
 * beside its `MouseSensor`. The pair is capability-based, so nothing gates
 * drag on viewport width: a mouse drags after a short travel, a finger drags
 * after a hold, and a finger that travels past the tolerance before the hold
 * ends is scrolling. `PointerSensor` alone cannot express this — it fires for
 * touch too and would start a drag on the first swipe.
 */
// Sits just past the touch arm (`ARM_HOLD_MS`, 300): a finger held on a
// card sees its chrome appear first, then the card lift — Linear's ladder —
// and stays short of the browser's 500ms long press.
export const DND_TOUCH_ACTIVATION = { delay: 400, tolerance: 5 } as const;

/**
 * Whether a dnd-kit drag is in flight anywhere on the page: every drag host
 * mounts its lifted clone with `DND_OVERLAY_PROPS`, so the marker's presence
 * IS the drag. Read at the moment a question is asked (a context menu about
 * to open), never cached.
 *
 * The question it answers: a finger's hold lifts a card at 400ms
 * (`DND_TOUCH_ACTIVATION`), and a context-menu trigger's own long press
 * fires at 500ms on a timer nothing else clears — so without this check the
 * menu opens over the drag the same finger is already making. dnd-kit
 * prevents the NATIVE `contextmenu` while dragging; this covers the
 * synthetic long press.
 */
export function isDragInFlight(): boolean {
  return (
    typeof document !== "undefined" &&
    document.querySelector("[data-dnd-overlay]") !== null
  );
}

/**
 * dnd-kit's activator listeners with the touch one moved to the CAPTURE
 * phase. A context-menu trigger inside a draggable (a card's menu wraps the
 * card body) calls `stopPropagation` on `touchstart` for its own long
 * press, so a bubble-phase listener on the draggable never hears the finger
 * and the card cannot be dragged by touch at all. Captured, the activator
 * runs first and both timers start: dnd lifts at 400ms, and the menu's
 * 500ms long press is then refused by `ContextMenu` (`isDragInFlight`).
 * The mouse and keyboard activators stay in the bubble phase: a control
 * inside the draggable keeps the right to claim its own click.
 */
export function withTouchCapture(
  listeners: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (!listeners) return {};
  const { onTouchStart, ...rest } = listeners;
  return onTouchStart ? { ...rest, onTouchStartCapture: onTouchStart } : rest;
}
